import { format, startOfWeek, startOfMonth } from "date-fns";
import { LineChartDataPoint } from "../../LineChart";

export interface RawDataPoint {
  date: Date;
  value: number;
}

export type DataInterval = "daily" | "weekly" | "monthly";

/**
 * Aggregates raw data points based on the specified interval
 * Marks ongoing periods (current week/month) as partial for dashed line rendering
 */
export const aggregateData = (
  data: RawDataPoint[],
  interval: DataInterval,
): LineChartDataPoint[] => {
  if (interval === "daily") {
    return data.map((d) => ({
      ...d,
      date: format(d.date, "MMM d"),
    }));
  }

  const groupedData = new Map<
    string,
    {
      value: number;
      dates: Date[];
      periodStart: Date;
    }
  >();

  // Group data by time period
  data.forEach((point) => {
    const periodStart =
      interval === "weekly"
        ? startOfWeek(point.date, { weekStartsOn: 1 })
        : startOfMonth(point.date);

    const key =
      interval === "weekly"
        ? format(periodStart, "MMM d, yyyy")
        : format(periodStart, "MMM yyyy");

    const existing = groupedData.get(key);
    if (existing) {
      existing.value += point.value;
      existing.dates.push(point.date);
    } else {
      groupedData.set(key, {
        value: point.value,
        dates: [point.date],
        periodStart,
      });
    }
  });

  // Convert to chart data and mark partial periods
  const now = new Date();
  const currentPeriodStart =
    interval === "weekly"
      ? startOfWeek(now, { weekStartsOn: 1 })
      : startOfMonth(now);

  return Array.from(groupedData.entries())
    .map(([date, { value, periodStart }]) => ({
      date,
      value,
      isPartial: periodStart.getTime() === currentPeriodStart.getTime(),
      periodStart,
    }))
    .sort((a, b) => a.periodStart.getTime() - b.periodStart.getTime());
};
